import React from "react";
import { StudentBar, EmptyStudentBar } from "./StudentBar";
import { Trans, useTranslation } from "react-i18next";
import { Button } from "@/components/ui/Button";
import { X } from "lucide-react";
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/Dialog";
import { Student } from "@/__generated__/graphql";

interface CsvUploadResultsProps {
  students: Student[];
  missingIds?: number[];
  resetUpload?: () => void;
  csvFile?: File;
}

export function CsvUploadResults({
  students,
  missingIds,
  resetUpload,
  csvFile,
}: CsvUploadResultsProps) {
  const { t } = useTranslation();

  if (!students.length) {
    return <NoneFound csvFile={csvFile} resetUpload={resetUpload} />;
  }

  return (
    <section
      className="flex flex-col gap-6 rounded bg-gray-50 p-8 shadow-sm dark:bg-input"
      data-testid="csv-upload-results"
    >
      <div className="flex items-center gap-4">
        <StudentBar students={students}>
          {missingIds?.length ? <MissingLink missingIds={missingIds} /> : <></>}
        </StudentBar>
      </div>
      <div className="flex items-center gap-4">
        <Button
          className="w-fit"
          variant="outline"
          type="button"
          onClick={resetUpload}
        >
          <X />
          {t("bulkMessaging.basicInformation.csvImport.removeStudents")}
        </Button>
        {csvFile ? (
          <p className="text-sm text-foreground/40">{csvFile?.name}</p>
        ) : (
          <></>
        )}
      </div>
    </section>
  );
}

function MissingLink({ missingIds }: { missingIds: number[] }) {
  const { t } = useTranslation();

  return (
    <Dialog>
      <DialogTrigger className="justify-self-start text-sm text-foreground underline">
        <h2 data-testid="students-not-found-button">
          <Trans
            i18nKey="bulkMessaging.basicInformation.csvImport.studentsNotFound"
            count={missingIds.length}
            values={{ count: missingIds.length.toLocaleString() }}
          >
            <strong className="font-medium"></strong> Students Not Found
          </Trans>
        </h2>
      </DialogTrigger>
      <DialogContent>
        <DialogTitle className="text-sm font-medium">
          {t("bulkMessaging.basicInformation.csvImport.missingDialogTitle")}
        </DialogTitle>
        <DialogDescription className="text-sm">
          {t(
            "bulkMessaging.basicInformation.csvImport.missingDialogDescription",
          )}
        </DialogDescription>
        <ul data-testid="id-list" className="list-inside list-disc text-sm">
          {missingIds.map((id) => (
            <li key={id}>{id}</li>
          ))}
        </ul>
      </DialogContent>
    </Dialog>
  );
}

interface NoneFoundProps {
  resetUpload?: () => void;
  csvFile?: File;
}

function NoneFound({ csvFile, resetUpload }: NoneFoundProps) {
  const { t } = useTranslation();

  return (
    <section className="flex h-60 flex-col gap-6 rounded bg-gray-50 p-8 shadow-sm dark:bg-input">
      <EmptyStudentBar />
      <p className="text-sm text-foreground/60">
        <Trans i18nKey="bulkMessaging.basicInformation.csvImport.missingIds">
          <strong className="font-medium">Reminder:</strong> You must upload a
          CSV of existing ZeeMee users
        </Trans>
      </p>
      <div className="flex items-center gap-4">
        <Button
          className="w-fit"
          variant="outline"
          type="button"
          onClick={resetUpload}
        >
          <X />
          {t("bulkMessaging.basicInformation.csvImport.tryAgain")}
        </Button>
        {csvFile ? (
          <p className="text-sm text-foreground/40">{csvFile?.name}</p>
        ) : (
          <></>
        )}
      </div>
    </section>
  );
}
